import { Grid } from '@material-ui/core';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { extractErrorMessage } from '../helpers/apiUtils';
import AuthStore from '../store/AuthStore';
import FirstTimeContext from '../store/FirstTimeContext';
import GlimraStore from '../store/GlimraStore';
import FbPixel from '../trackers/FbPixel';
import { BankIDSession } from '../types/bankID';
import { CompanyRegistrationData, UserRegistrationData } from '../types/registration';

import { BankID } from './BankID';
import CompanyRegistrationForm from './CompanyRegistrationForm';
import { snackRef } from './Snack';
import UserRegistrationForm from './UserRegistrationForm';

const COMPANY_CREATION_BANKID_LEGAL_TEXT_KEY = 'business_portal_company_creation';

const RegisterForm = ({ login }: { login: (email: string, password: string) => void }) => {
  const setFirstTime = useContext(FirstTimeContext).setFirstTime;
  const [loading, setLoading] = useState(false);
  const [signupStage, setSignupStage] = useState<'companyFilledIn' | 'userLoggedIn'>();
  const [companyData, setCompanyData] = useState<CompanyRegistrationData>();
  const [userData, setUserData] = useState<UserRegistrationData>();
  const { t } = useTranslation();

  const createCompany = (companyData: CompanyRegistrationData) => {
    setLoading(true);

    try {
      GlimraStore.createCompany(companyData);
      snackRef.current.updateSnack({
        variant: 'success',
        title: t('registerForm.createCompanySuccess.title'),
        message: t('registerForm.createCompanySuccess.message'),
      });

      // @ts-ignore
      setFirstTime(true);

      FbPixel.trackCompanyRegistered();

      if (userData) return login(userData?.email, userData?.password);
    } catch (error) {
      snackRef.current.updateSnack({
        variant: 'error',
        title: t('registerForm.createCompanyError.title'),
        message: extractErrorMessage(error, t('registerForm.createCompanyError.message')),
      });
    } finally {
      setLoading(false);
    }
  };

  const onCompanyRegistration = (companyData: CompanyRegistrationData) => {
    setCompanyData(companyData);
    setSignupStage('companyFilledIn');
  };

  const onUserRegistration = async (
    email: string,
    password: string,
    verification_code?: string
  ) => {
    try {
      await AuthStore.login(email, password);
      setUserData({ email, password, verification_code });
      setSignupStage('userLoggedIn');
    } catch (error) {
      snackRef.current.updateSnack({
        variant: 'error',
        title: t('landingPage.loginError.title'),
        message: t('landingPage.loginError.messageUnknownError'),
      });
    }
  };

  const onBankIDSuccess = (session: BankIDSession) => {
    if (!companyData) return;
    createCompany({
      ...companyData,
      verification_code: userData?.verification_code,
      bank_id_session_uuid: session.uuid,
    });
  };

  const onReset = () => {
    setSignupStage(undefined);
  };

  return (
    <Grid container spacing={4} justify="center">
      {signupStage === undefined && (
        <CompanyRegistrationForm onRegister={onCompanyRegistration} initalValues={companyData} />
      )}
      {signupStage === 'companyFilledIn' && (
        <UserRegistrationForm loading={loading} onRegister={onUserRegistration} onReset={onReset} />
      )}
      {signupStage === 'userLoggedIn' && (
        <BankID
          legal_text_key={COMPANY_CREATION_BANKID_LEGAL_TEXT_KEY}
          onSuccess={onBankIDSuccess}
          onReset={onReset}
        />
      )}
    </Grid>
  );
};

export default RegisterForm;
